import { useState, useEffect, useCallback } from 'react'
import { cmsService } from '../../services/cmsService'
import { Field, Input, Textarea, SectionCard, PageHeader, SpinnerIcon } from '../../components/cms/shared'
import RichTextEditor from '../../components/cms/RichTextEditor'

/**
 * Generic CMS editor for legal document pages.
 * Each section has a title + HTML content textarea.
 * sections: [{ id, label }]
 */
export default function LegalPageEditor({ page, liveUrl, pageTitle, pageDescription, sections }) {
    const [hero, setHero] = useState({ title: '', description: '', badges: [] })
    const [sectionContent, setSectionContent] = useState({})
    const [pageLoading, setPageLoading] = useState(true)

    const initState = () => {
        const s = { hero: { saving: false, saved: false, error: null } }
        sections.forEach((sec) => { s[sec.id] = { saving: false, saved: false, error: null } })
        return s
    }
    const [sectionState, setSectionState] = useState(initState)

    useEffect(() => {
        cmsService.getPage(page).then((res) => {
            if (res?.data) {
                const d = res.data
                if (d.hero) setHero({ title: '', description: '', badges: [], ...d.hero })
                const map = {}
                sections.forEach((sec) => {
                    if (d[sec.id]) map[sec.id] = { title: d[sec.id].title || sec.label, content: d[sec.id].content || '' }
                    else map[sec.id] = { title: sec.label, content: '' }
                })
                setSectionContent(map)
            }
        }).catch(() => {}).finally(() => setPageLoading(false))
    }, [page])

    const setSaving = (sectionId, state) =>
        setSectionState((prev) => ({ ...prev, [sectionId]: { ...prev[sectionId], ...state } }))

    const save = useCallback(async (sectionId, payload) => {
        setSaving(sectionId, { saving: true, saved: false, error: null })
        try {
            await cmsService.updateSection(page, sectionId, payload)
            setSaving(sectionId, { saving: false, saved: true, error: null })
            setTimeout(() => setSaving(sectionId, { saved: false }), 3000)
        } catch (err) {
            setSaving(sectionId, { saving: false, saved: false, error: err.message || 'Save failed' })
        }
    }, [page])

    const setHeroField = (field, value) => setHero((h) => ({ ...h, [field]: value }))
    const updateSection = (id, field, value) =>
        setSectionContent((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }))

    const updateBadge = (i, value) => {
        const badges = [...(hero.badges || [])]
        badges[i] = value
        setHeroField('badges', badges)
    }

    if (pageLoading) return <div className="flex items-center justify-center py-24"><SpinnerIcon className="w-6 h-6 animate-spin text-brand-navy" /></div>

    return (
        <div className="space-y-6">
            <PageHeader title={pageTitle} description={pageDescription} liveUrl={liveUrl} />

            {/* Hero */}
            <SectionCard title="Hero Banner" onSave={() => save('hero', hero)} {...sectionState.hero}>
                <Field label="Page Title"><Input value={hero.title} onChange={(v) => setHeroField('title', v)} /></Field>
                {hero.description !== undefined && (
                    <Field label="Description"><Textarea value={hero.description} onChange={(v) => setHeroField('description', v)} rows={2} /></Field>
                )}
                {(hero.badges || []).length > 0 && (
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-admin-muted uppercase tracking-wider">Badge Pills</label>
                        {hero.badges.map((badge, i) => (
                            <Input key={i} value={badge} onChange={(v) => updateBadge(i, v)} />
                        ))}
                    </div>
                )}
            </SectionCard>

            {/* Legal sections */}
            {sections.map((sec) => {
                const data = sectionContent[sec.id] || { title: sec.label, content: '' }
                return (
                    <SectionCard
                        key={sec.id}
                        title={sec.label}
                        description="Edit the section title and HTML content."
                        onSave={() => save(sec.id, data)}
                        {...(sectionState[sec.id] || {})}
                    >
                        <Field label="Section Title">
                            <Input value={data.title} onChange={(v) => updateSection(sec.id, 'title', v)} />
                        </Field>
                        <Field label="Content">
                            <RichTextEditor
                                value={data.content}
                                onChange={(v) => updateSection(sec.id, 'content', v)}
                            />
                        </Field>
                    </SectionCard>
                )
            })}
        </div>
    )
}
